import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { API_BASE } from '../../config';
import { useTranslation } from '../../translations';
import { useToast } from '../ui/Toast';
import { useConfirm } from '../ui/Modal';
import { PrimaryButton, SecondaryButton, DangerButton, TextArea } from '../../ui/common';
import { ulozKandydatow, opisCelu } from '../../utils/doborSprzetu';

// Zapytanie z widgetu o sprzęt z klas (26.10.2026, migracja 069), w szczegółach rezerwacji.
// Klient zamówił klasy i podał wymiary; tu pracownik dobiera konkretne sztuki, a potem potwierdza
// dostępność (klient dostaje mail z linkiem do zapłaty albo danymi do przelewu, a gdy firma nie ma
// ani jednego, ani drugiego, `payment_details: false`, prośbę o kontakt) albo odrzuca.
// Wybór sztuki rozwija się POD pozycją, nie w liście pływającej: okno szczegółów jest osobną
// nakładką i pływająca lista lądowałaby pod nią, a na telefonie i tak zasłaniałaby wymiary.
// API: /api/class-booking/reservations/:id (Login/backend/routes/classBooking.js).

const POKAZ_NA_START = 6;

const csrf = () => localStorage.getItem('csrfToken') || '';

async function wyslij(url, cialo) {
  const r = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'x-csrf-token': csrf() },
    body: JSON.stringify(cialo || {}),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) {
    const e = new Error(d.error || '');
    e.status = r.status;
    throw e;
  }
  return d;
}

export default function ClassRequestPanel({ reservationId, refreshKey }) {
  const { t } = useTranslation();
  const toast = useToast();
  const confirm = useConfirm();
  const [dane, setDane] = useState(null);
  const [stan, setStan] = useState('loading'); // loading | ready | error
  const [otwarta, setOtwarta] = useState(null); // id sztuki z rozwiniętym wyborem
  const [wszystkie, setWszystkie] = useState(false);
  const [trwa, setTrwa] = useState(''); // '' | 'assign:<id>' | 'confirm' | 'decline'
  const [blad, setBlad] = useState('');
  const [odmowa, setOdmowa] = useState({ otwarta: false, tekst: '' });

  const wczytaj = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/api/class-booking/reservations/${reservationId}`, { credentials: 'include' });
      if (!r.ok) throw new Error(String(r.status));
      setDane(await r.json());
      setStan('ready');
    } catch (_) {
      setStan('error');
    }
  }, [reservationId]);

  useEffect(() => { wczytaj(); }, [wczytaj, refreshKey]);

  const grupy = useMemo(() => {
    const mapa = new Map();
    for (const u of (dane && dane.units) || []) {
      const klucz = `${u.type_name} · ${u.class_name}`;
      if (!mapa.has(klucz)) mapa.set(klucz, []);
      mapa.get(klucz).push(u);
    }
    return [...mapa.entries()];
  }, [dane]);

  if (stan === 'loading') return <Cicho>{t('classRequest.loading')}</Cicho>;
  if (stan === 'error') return <Cicho $blad role="alert">{t('classRequest.loadError')}</Cicho>;
  if (!dane || !dane.units.length) return null;

  const czeka = dane.request === 'awaiting' && !dane.cancelled;
  const potwierdzone = dane.request === 'confirmed' && !dane.cancelled;
  const bezDanych = dane.payment_details === false;
  const dobrane = dane.units.filter((u) => u.equipment).length;
  const przypisaneSztuki = new Set(dane.units.filter((u) => u.equipment).map((u) => u.equipment.id));
  // Sztuka dobrana już do innej pozycji tego zapytania znika z listy (poza tą, która ją ma).
  const kandydaciDla = (u) => ulozKandydatow(dane.candidates, u)
    .filter((e) => !przypisaneSztuki.has(e.id) || (u.equipment && u.equipment.id === e.id));

  const przypisz = async (unit, equipmentId) => {
    setBlad('');
    setTrwa(`assign:${unit.id}`);
    try {
      await wyslij(`${API_BASE}/api/class-booking/reservations/${reservationId}/assign`, { unit_id: unit.id, equipment_id: equipmentId });
      setOtwarta(null);
      setWszystkie(false);
      await wczytaj();
    } catch (e) {
      setBlad(e.message || t('classRequest.assignError'));
    } finally {
      setTrwa('');
    }
  };

  const potwierdz = async () => {
    const brakuje = dane.units.length - dobrane;
    if (brakuje > 0) {
      const ok = await confirm({
        title: t('classRequest.confirmMissingTitle'),
        message: t('classRequest.confirmMissingBody', { n: brakuje }),
        confirmLabel: t('classRequest.confirmAnyway'),
        cancelLabel: t('classRequest.keepAssigning'),
        tone: 'primary',
      });
      if (!ok) return;
    }
    setBlad('');
    setTrwa('confirm');
    try {
      const d = await wyslij(`${API_BASE}/api/class-booking/reservations/${reservationId}/confirm`);
      // Mail nie wyszedł: drugiej próby nie ma (potwierdzenie jest jednorazowe), a pracownik musi
      // zadzwonić do klienta, więc komunikat zostaje w panelu, zamiast zniknąć po kilku sekundach.
      if (d.emailSent === false) setBlad(t('classRequest.confirmedNoMail'));
      else if (d.method === 'online') toast.success(t('classRequest.confirmedOnline'));
      else if (d.method === 'contact') toast.success(t('classRequest.confirmedContact'));
      else toast.success(t('classRequest.confirmedTransfer'));
      await wczytaj();
    } catch (e) {
      setBlad(e.message || t('classRequest.confirmError'));
    } finally {
      setTrwa('');
    }
  };

  const odrzuc = async () => {
    setBlad('');
    setTrwa('decline');
    try {
      const d = await wyslij(`${API_BASE}/api/class-booking/reservations/${reservationId}/decline`, { message: odmowa.tekst });
      toast.success(d.emailSent ? t('classRequest.declined') : t('classRequest.declinedNoMail'));
      setOdmowa({ otwarta: false, tekst: '' });
      await wczytaj();
    } catch (e) {
      setBlad(e.message || t('classRequest.declineError'));
    } finally {
      setTrwa('');
    }
  };

  const etykietaSztuki = (e) => [e.name, e.size ? e.size : null, e.inventory_number ? t('classRequest.number', { n: e.inventory_number }) : null]
    .filter(Boolean).join(' · ');

  return (
    <Panel data-cy="class-request">
      <Glowa>
        <Tytul>{t('classRequest.title')}</Tytul>
        <Stan $ton={dane.cancelled ? 'muted' : (czeka ? 'warn' : 'ok')} data-cy="class-request-state">
          {dane.cancelled ? t('classRequest.stateCancelled') : (czeka ? t('classRequest.stateAwaiting') : t('classRequest.stateConfirmed'))}
        </Stan>
      </Glowa>
      {czeka && <Wstep data-cy="class-request-intro">{bezDanych ? t('classRequest.introNoPaymentDetails') : t('classRequest.intro')}</Wstep>}
      {potwierdzone && <Wstep>{t('classRequest.introConfirmed')}</Wstep>}

      <Postep aria-label={t('classRequest.progress', { done: dobrane, total: dane.units.length })}>
        <Odcinki aria-hidden="true">
          {dane.units.map((u) => <Odcinek key={u.id} $pelny={!!u.equipment} />)}
        </Odcinki>
        <PostepTekst>{t('classRequest.progress', { done: dobrane, total: dane.units.length })}</PostepTekst>
      </Postep>

      {grupy.map(([nazwa, units]) => (
        <Grupa key={nazwa}>
          <GrupaNazwa>{nazwa} <span>× {units.length}</span></GrupaNazwa>
          <Lista>
            {units.map((u, i) => {
              const cel = opisCelu(u);
              const kandydaci = kandydaciDla(u);
              const widoczni = wszystkie ? kandydaci : kandydaci.slice(0, POKAZ_NA_START);
              const rozwinieta = otwarta === u.id;
              return (
                <Pozycja key={u.id} data-cy="class-request-unit">
                  <Numer aria-hidden="true">{i + 1}</Numer>
                  <PozycjaTresc>
                    <Wymiary>
                      {u.for_whom && <Kto>{u.for_whom}</Kto>}
                      {u.answers_text
                        ? u.answers_text.split(', ').map((x) => <Wymiar key={x}>{x}</Wymiar>)
                        : <Cicho as="span">{t('classRequest.noAnswers')}</Cicho>}
                    </Wymiary>
                    {u.equipment ? (
                      <Przypisane data-cy="class-request-assigned">
                        <span>{etykietaSztuki(u.equipment)}</span>
                        {u.equipment.class_name && u.equipment.class_name !== u.class_name && (
                          <InnaKlasa>{t('classRequest.otherClass', { name: u.equipment.class_name })}</InnaKlasa>
                        )}
                        {!dane.cancelled && !u.equipment.picked_up && (
                          <Link type="button" onClick={() => { setOtwarta(rozwinieta ? null : u.id); setWszystkie(false); }} aria-expanded={rozwinieta}>
                            {t('classRequest.change')}
                          </Link>
                        )}
                      </Przypisane>
                    ) : (!dane.cancelled && (
                      <Dobierz>
                        <SecondaryButton
                          type="button"
                          onClick={() => { setOtwarta(rozwinieta ? null : u.id); setWszystkie(false); }}
                          aria-expanded={rozwinieta}
                          data-cy="class-request-pick"
                        >
                          {t('classRequest.pick')}
                        </SecondaryButton>
                        {cel && <Cel>{t('classRequest.target', { size: cel })}</Cel>}
                      </Dobierz>
                    ))}

                    {rozwinieta && (
                      <Wybor role="group" aria-label={t('classRequest.pickFor', { n: i + 1, name: nazwa })}>
                        {kandydaci.length === 0 && <Cicho>{t('classRequest.noCandidates')}</Cicho>}
                        {widoczni.map((e) => (
                          <Kandydat
                            key={e.id}
                            type="button"
                            $wybrany={u.equipment && u.equipment.id === e.id}
                            disabled={trwa === `assign:${u.id}`}
                            onClick={() => przypisz(u, e.id)}
                            data-cy="class-request-candidate"
                          >
                            <span className="nazwa">{etykietaSztuki(e)}</span>
                            <span className="znaczniki">
                              {e.class_id !== u.class_id && <InnaKlasa>{e.class_name}</InnaKlasa>}
                              {e.fit === 'best' && <Pasuje>{t('classRequest.fitBest')}</Pasuje>}
                              {e.fit === 'near' && <Blisko>{t('classRequest.fitNear')}</Blisko>}
                            </span>
                          </Kandydat>
                        ))}
                        <WyborStopka>
                          {!wszystkie && kandydaci.length > POKAZ_NA_START && (
                            <Link type="button" onClick={() => setWszystkie(true)}>
                              {t('classRequest.showAll', { n: kandydaci.length })}
                            </Link>
                          )}
                          {u.equipment && !u.equipment.picked_up && (
                            <Link type="button" onClick={() => przypisz(u, null)} data-cy="class-request-unassign">
                              {t('classRequest.unassign')}
                            </Link>
                          )}
                        </WyborStopka>
                      </Wybor>
                    )}
                  </PozycjaTresc>
                </Pozycja>
              );
            })}
          </Lista>
        </Grupa>
      ))}

      {blad && <Blad role="alert" data-cy="class-request-error">{blad}</Blad>}

      {czeka && !odmowa.otwarta && (
        <Akcje>
          <PrimaryButton type="button" onClick={potwierdz} disabled={!!trwa} data-cy="class-request-confirm">
            {trwa === 'confirm' && t('classRequest.confirming')}
            {trwa !== 'confirm' && (bezDanych ? t('classRequest.confirmNoPaymentDetails') : t('classRequest.confirm'))}
          </PrimaryButton>
          <SecondaryButton type="button" onClick={() => setOdmowa({ otwarta: true, tekst: '' })} disabled={!!trwa} data-cy="class-request-decline-open">
            {t('classRequest.decline')}
          </SecondaryButton>
        </Akcje>
      )}
      {czeka && odmowa.otwarta && (
        <Odmowa>
          <label htmlFor={`odmowa-${reservationId}`}>{t('classRequest.declineMessage')}</label>
          <TextArea
            id={`odmowa-${reservationId}`}
            rows={3}
            maxLength={500}
            value={odmowa.tekst}
            onChange={(e) => setOdmowa({ otwarta: true, tekst: e.target.value })}
            placeholder={t('classRequest.declinePlaceholder')}
            data-cy="class-request-decline-message"
          />
          <Akcje>
            <DangerButton type="button" onClick={odrzuc} disabled={!!trwa} data-cy="class-request-decline">
              {trwa === 'decline' ? t('classRequest.declining') : t('classRequest.declineSend')}
            </DangerButton>
            <SecondaryButton type="button" onClick={() => setOdmowa({ otwarta: false, tekst: '' })} disabled={!!trwa}>
              {t('classRequest.back')}
            </SecondaryButton>
          </Akcje>
        </Odmowa>
      )}
    </Panel>
  );
}

/* ── Wygląd ─────────────────────────────────────────────────────────────────────────────── */

const TONY = {
  warn: 'var(--warning)',
  ok: 'var(--success)',
  muted: 'var(--text-muted)',
};

const Panel = styled.section`
  min-width: 0;
  padding: 18px 20px;
  border: 1px solid color-mix(in srgb, var(--warning) 35%, var(--input-border));
  border-radius: 16px;
  background: var(--panel-bg);
`;

const Glowa = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const Tytul = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: var(--text);
`;

const Stan = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  color: ${(p) => TONY[p.$ton]};
  background: color-mix(in srgb, ${(p) => TONY[p.$ton]} 12%, transparent);
  &::before { content: ''; width: 6px; height: 6px; border-radius: 50%; background: currentColor; }
`;

const Wstep = styled.p`
  margin: 8px 0 0;
  font-size: 0.875rem;
  line-height: 1.45;
  color: var(--text-muted);
`;

const Postep = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 14px 0 4px;
`;

const Odcinki = styled.div`
  display: flex;
  gap: 3px;
  flex: 1;
  max-width: 220px;
`;

const Odcinek = styled.span`
  flex: 1;
  height: 6px;
  border-radius: 3px;
  background: ${(p) => (p.$pelny ? 'var(--success)' : 'var(--input-border)')};
`;

const PostepTekst = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
`;

const Grupa = styled.div`
  margin-top: 14px;
`;

const GrupaNazwa = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--text-muted);
  span { font-variant-numeric: tabular-nums; }
`;

const Lista = styled.ol`
  list-style: none;
  margin: 6px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Pozycja = styled.li`
  display: grid;
  grid-template-columns: 26px minmax(0, 1fr);
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid var(--input-border);
  border-radius: 12px;
  background: var(--card-bg);
`;

const Numer = styled.span`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--text);
  background: var(--hover-bg);
  font-variant-numeric: tabular-nums;
`;

const PozycjaTresc = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Wymiary = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
`;

const Kto = styled.span`
  font-weight: 700;
  color: var(--text);
  margin-right: 2px;
`;

// Wymiar jak metka: liczba z jednostką, czytelna z daleka przy ladzie.
const Wymiar = styled.span`
  padding: 2px 8px;
  border-radius: 6px;
  border: 1px solid var(--input-border);
  font-size: 0.8125rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: var(--text);
`;

const Dobierz = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;

  button { padding: 7px 14px; min-height: 0; font-size: 0.875rem; }
`;

const Cel = styled.span`
  font-size: 0.8125rem;
  color: var(--text-muted);
`;

const Przypisane = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text);

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--success);
    flex: none;
  }
`;

const InnaKlasa = styled.span`
  padding: 1px 7px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--text-muted);
  background: var(--hover-bg);
`;

const Link = styled.button`
  border: 0;
  background: none;
  padding: 2px 0;
  font: inherit;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--primary);
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
  &:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; border-radius: 4px; }
`;

const Wybor = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px;
  border-radius: 10px;
  background: var(--hover-bg);
`;

const Kandydat = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid ${(p) => (p.$wybrany ? 'var(--primary)' : 'transparent')};
  background: var(--card-bg);
  color: var(--text);
  font: inherit;
  font-size: 0.875rem;
  text-align: left;
  cursor: pointer;

  .nazwa { min-width: 0; overflow-wrap: anywhere; }
  .znaczniki { display: inline-flex; gap: 6px; flex: none; }
  &:hover:not(:disabled) { border-color: var(--primary); }
  &:focus-visible { outline: 2px solid var(--primary); outline-offset: 1px; }
  &:disabled { opacity: 0.6; cursor: progress; }
`;

const Pasuje = styled.span`
  padding: 1px 7px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  color: var(--success);
  background: color-mix(in srgb, var(--success) 12%, transparent);
`;

const Blisko = styled(Pasuje)`
  color: var(--warning);
  background: color-mix(in srgb, var(--warning) 12%, transparent);
`;

const WyborStopka = styled.div`
  display: flex;
  gap: 16px;
  padding: 4px 2px 0;
  &:empty { display: none; }
`;

const Akcje = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 16px;
`;

const Odmowa = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;

  label { font-size: 0.8125rem; font-weight: 600; color: var(--text); }
  ${Akcje} { margin-top: 6px; }
`;

const Blad = styled.div`
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  background: color-mix(in srgb, var(--danger) 10%, transparent);
  color: var(--danger);
  font-size: 0.875rem;
`;

const Cicho = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: ${(p) => (p.$blad ? 'var(--danger)' : 'var(--text-muted)')};
`;
